/**
 * Candlestick chart for one RSI(2) Oversold Pullback idea (Investment
 * Ideas — read-only research/educational chart). NEVER shows a buy/sell
 * signal — only OHLC candles, SMA(50)/SMA(200) overlays, the Reference
 * Price / Indicative Research Target / Indicative Research Risk Level as
 * horizontal reference lines, and Signal-Date / Target-Hit-Date vertical
 * markers.
 *
 * No candlestick chart component exists anywhere else in this codebase,
 * and only chart.js + react-chartjs-2 are installed (no
 * chartjs-chart-financial / lightweight-charts / recharts / apexcharts,
 * and none is being added here per instruction). The candlestick is built
 * from two overlaid Chart.js "bar" datasets on a category x-axis (dates as
 * labels): a thin "wick" bar (`[low, high]`) and a wider "body" bar
 * (`[min(open,close), max(open,close)]`, colored per-candle green/red).
 * `scales.x.grouped = false` makes both bars render centered on the same
 * category instead of Chart.js's default side-by-side grouping.
 *
 * SMA/reference-line overlays are plain "line" datasets sharing the same
 * category x-axis, mixed into this same Bar-type chart via react-chartjs-2's
 * generic `<Chart type="bar">` (each line dataset declares its own
 * `type: "line"` override) — the same "typed `any` for the scriptable
 * Chart.js data/options block" convention `PayoffChart.tsx` already uses.
 */
import { useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
  type Chart as ChartInstance,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import type { Rsi2PullbackChartCandle } from "@/types";
import {
  buildVerticalMarkerPlugin,
  COLOR_BODY_DOWN,
  COLOR_BODY_UP,
  COLOR_REFERENCE,
  COLOR_RISK,
  COLOR_SIGNAL_MARKER,
  COLOR_SMA_50,
  COLOR_SMA_200,
  COLOR_TARGET,
  COLOR_TARGET_HIT_MARKER,
  COLOR_WICK,
  formatDateDDMMYY,
  type VerticalMarker,
} from "./chartPluginsAndColors";

ChartJS.register(
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  ChartTooltip,
  Legend
);

const CANDLE_MARKER_PLUGIN_ID = "rsi2CandleVerticalMarkers";
const candleVerticalMarkerPlugin = buildVerticalMarkerPlugin(CANDLE_MARKER_PLUGIN_ID);

function toNum(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = Number.parseFloat(value);
  return Number.isNaN(n) ? null : n;
}

export interface Rsi2CandleChartProps {
  candles: Rsi2PullbackChartCandle[];
  sma50: (string | null)[];
  sma200: (string | null)[];
  signalDate: string;
  referencePrice?: string | null;
  indicativeTargetPrice?: string | null;
  indicativeRiskLevel?: string | null;
  targetHitDate?: string | null;
}

export function Rsi2CandleChart({
  candles,
  sma50,
  sma200,
  signalDate,
  referencePrice,
  indicativeTargetPrice,
  indicativeRiskLevel,
  targetHitDate,
}: Rsi2CandleChartProps) {
  /* eslint-disable @typescript-eslint/no-explicit-any -- Chart.js mixed
   * bar/line dataset typing (per-dataset `type` override, scriptable
   * per-point colors) is stricter than this file needs; `PayoffChart.tsx`
   * already casts `data`/`options` to `any` at the call site for the same
   * reason. */
  const chartRef = useRef<ChartInstance<any> | null>(null);

  const labels = useMemo(() => candles.map((c) => c.trade_date), [candles]);

  const signalIndex = labels.indexOf(signalDate);
  const targetHitIndex = targetHitDate ? labels.indexOf(targetHitDate) : -1;

  const markers: VerticalMarker[] = useMemo(() => {
    const list: VerticalMarker[] = [];
    if (signalIndex >= 0) {
      list.push({ index: signalIndex, color: COLOR_SIGNAL_MARKER, label: "Setup" });
    }
    // Never fabricate a Target Hit marker -- only rendered when the backend
    // actually returned a `target_hit_date` (a still-PENDING idea has none).
    if (targetHitDate && targetHitIndex >= 0) {
      list.push({ index: targetHitIndex, color: COLOR_TARGET_HIT_MARKER, label: "Target Hit" });
    }
    return list;
  }, [signalIndex, targetHitDate, targetHitIndex]);

  const data: any = useMemo(() => {
    const wickData = candles.map((c) => [toNum(c.low), toNum(c.high)]);
    const bodyData = candles.map((c) => {
      const open = toNum(c.open) ?? 0;
      const close = toNum(c.close) ?? 0;
      return [Math.min(open, close), Math.max(open, close)];
    });
    const bodyColors = candles.map((c) => {
      const open = toNum(c.open) ?? 0;
      const close = toNum(c.close) ?? 0;
      return close >= open ? COLOR_BODY_UP : COLOR_BODY_DOWN;
    });

    const datasets: any[] = [
      {
        type: "bar",
        label: "Wick",
        data: wickData,
        backgroundColor: COLOR_WICK,
        barThickness: 2,
        order: 1,
      },
      {
        type: "bar",
        label: "Price",
        data: bodyData,
        backgroundColor: bodyColors,
        barThickness: 8,
        order: 0,
      },
      {
        type: "line",
        label: "SMA(50)",
        data: sma50.map(toNum),
        borderColor: COLOR_SMA_50,
        borderWidth: 1.5,
        pointRadius: 0,
        spanGaps: false,
        order: 2,
      },
      {
        type: "line",
        label: "SMA(200)",
        data: sma200.map(toNum),
        borderColor: COLOR_SMA_200,
        borderWidth: 1.5,
        pointRadius: 0,
        spanGaps: false,
        order: 2,
      },
    ];

    const refPrice = toNum(referencePrice);
    if (refPrice !== null) {
      datasets.push({
        type: "line",
        label: "Reference Price",
        data: labels.map(() => refPrice),
        borderColor: COLOR_REFERENCE,
        borderDash: [6, 4],
        borderWidth: 1,
        pointRadius: 0,
        order: 3,
      });
    }
    const target = toNum(indicativeTargetPrice);
    if (target !== null) {
      datasets.push({
        type: "line",
        label: "Indicative Research Target",
        data: labels.map(() => target),
        borderColor: COLOR_TARGET,
        borderDash: [2, 3],
        borderWidth: 1,
        pointRadius: 0,
        order: 3,
      });
    }
    const risk = toNum(indicativeRiskLevel);
    if (risk !== null) {
      datasets.push({
        type: "line",
        label: "Indicative Research Risk Level",
        data: labels.map(() => risk),
        borderColor: COLOR_RISK,
        borderDash: [2, 3],
        borderWidth: 1,
        pointRadius: 0,
        order: 3,
      });
    }

    return { labels, datasets };
  }, [candles, sma50, sma200, labels, referencePrice, indicativeTargetPrice, indicativeRiskLevel]);

  // Y-axis autoscale (spec item 9): base the visible price range on the
  // actual candles/levels currently rendered (already pre-windowed by
  // `ChartDialog`'s 3M/6M/1Y range toggle by the time they reach this
  // component) rather than letting Chart.js auto-scale off the full,
  // unfiltered dataset -- a long lookback with an old unrelated price
  // swing would otherwise flatten/distort the interesting signal region.
  const yRange = useMemo(() => {
    const highs = candles.map((c) => toNum(c.high)).filter((n): n is number => n !== null);
    const lows = candles.map((c) => toNum(c.low)).filter((n): n is number => n !== null);
    const levels = [toNum(referencePrice), toNum(indicativeTargetPrice), toNum(indicativeRiskLevel)].filter(
      (n): n is number => n !== null
    );
    const all = [...highs, ...lows, ...levels];
    if (all.length === 0) return undefined;
    const lo = Math.min(...all);
    const hi = Math.max(...all);
    const pad = (hi - lo) * 0.04 || hi * 0.04 || 1;
    return { min: lo - pad, max: hi + pad };
  }, [candles, referencePrice, indicativeTargetPrice, indicativeRiskLevel]);

  const options: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: "index", intersect: false },
      // Small right-side padding so the "Setup"/"Target Hit" vertical marker
      // labels (drawn just to the right of the line via `fillText`) are
      // never clipped at the chart edge when the marker falls near the last
      // visible bar.
      layout: { padding: { top: 4, right: 32, bottom: 0, left: 4 } },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            font: { size: 10 },
            // Simplified legend (spec item 11): only Price/SMA50/SMA200.
            // Reference/Target/Risk lines and the wick helper dataset stay
            // visible on the chart (with tooltips) but are dropped from the
            // legend -- their meaning is already covered by the KPI strip
            // and the dashed-line labels themselves.
            filter: (item: any) =>
              !["Wick", "Reference Price", "Indicative Research Target", "Indicative Research Risk Level"].includes(
                item.text
              ),
          },
        },
        tooltip: {
          callbacks: {
            label: (item: any) => {
              if (item.dataset.label === "Wick") return "";
              if (item.dataset.label === "Price") {
                const c = candles[item.dataIndex];
                if (!c) return "";
                return `O ${c.open} H ${c.high} L ${c.low} C ${c.close}`;
              }
              const v = item.parsed.y;
              return v == null ? "" : `${item.dataset.label}: ${v.toFixed(2)}`;
            },
          },
        },
        [CANDLE_MARKER_PLUGIN_ID]: { markers },
      },
      scales: {
        x: {
          type: "category",
          grouped: false,
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 12,
            font: { size: 10 },
            // dd/mm/yy display format (spec requirement) -- `this` is the
            // scale instance here (a regular function, not an arrow, so
            // `getLabelForValue` resolves the raw "YYYY-MM-DD" category
            // label for this tick's index before reformatting it).
            callback(this: { getLabelForValue: (value: number) => string }, value: number) {
              return formatDateDDMMYY(this.getLabelForValue(value));
            },
          },
        },
        y: {
          ...(yRange ?? {}),
          ticks: {
            font: { size: 10 },
            // No decimals in price axis labels (spec requirement) -- Chart.js's
            // default numeric formatter otherwise shows one decimal place
            // (e.g. "2,337.0") even when every tick value is a whole number.
            callback: (value: number | string) => Math.round(Number(value)).toLocaleString("en-IN"),
          },
        },
      },
    }),
    [candles, markers, yRange]
  );
  /* eslint-enable @typescript-eslint/no-explicit-any */

  if (candles.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No historical price data available to render this chart yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ height: 320 }}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <Chart type="bar" ref={chartRef as any} data={data} options={options} plugins={[candleVerticalMarkerPlugin]} />
    </Box>
  );
}

export default Rsi2CandleChart;
